import { client } from "./supabase";

/**
 * Dado un array de facturas_sii_emitidas vinculadas,
 * devuelve un Map<"venta_tipo-venta_ref_id-venta_ref_index", {plazocobro, facturafechacobro}>
 * cruzando con la tabla de venta origen de cada una.
 */
export const getPlazosCobroParaSII = async (siiRows) => {
  const result = new Map();
  if (!siiRows?.length) return result;

  const makeKey = (tipo, id, index) => `${tipo}-${id}-${index ?? ""}`;

  const porTipo = {};
  for (const f of siiRows) {
    if (!f.venta_tipo || !f.venta_ref_id) continue;
    if (!porTipo[f.venta_tipo]) porTipo[f.venta_tipo] = [];
    porTipo[f.venta_tipo].push(f);
  }

  if (porTipo.sales?.length) {
    const ids = [...new Set(porTipo.sales.map((f) => f.venta_ref_id))];
    const { data } = await client.from("sales").select("id, plazocobro, facturafechacobro, moneda_dolar").in("id", ids);
    for (const row of data || []) {
      for (const f of porTipo.sales.filter((x) => x.venta_ref_id === row.id)) {
        result.set(makeKey("sales", row.id, f.venta_ref_index), {
          plazocobro: row.plazocobro ?? null,
          facturafechacobro: row.facturafechacobro ?? null,
          moneda_dolar_origen: row.moneda_dolar ?? false,
        });
      }
    }
  }

  if (porTipo.sales_btl?.length) {
    const ids = [...new Set(porTipo.sales_btl.map((f) => f.venta_ref_id))];
    const { data } = await client.from("sales_btl").select("id, facturafechacobro").in("id", ids);
    for (const row of data || []) {
      for (const f of porTipo.sales_btl.filter((x) => x.venta_ref_id === row.id)) {
        result.set(makeKey("sales_btl", row.id, f.venta_ref_index), {
          plazocobro: null,
          facturafechacobro: row.facturafechacobro ?? null,
          moneda_dolar_origen: false,
        });
      }
    }
  }

  if (porTipo.sales_contratos?.length) {
    const ids = [...new Set(porTipo.sales_contratos.map((f) => f.venta_ref_id))];
    const { data } = await client.from("sales_contratos").select("id, meses, moneda_dolar").in("id", ids);
    for (const row of data || []) {
      for (const f of porTipo.sales_contratos.filter((x) => x.venta_ref_id === row.id)) {
        const mes = f.venta_ref_index != null ? (row.meses || [])[f.venta_ref_index] : null;
        result.set(makeKey("sales_contratos", row.id, f.venta_ref_index), {
          plazocobro: mes?.plazocobro ?? null,
          facturafechacobro: mes?.facturafechacobro ?? null,
          moneda_dolar_origen: row.moneda_dolar ?? false,
        });
      }
    }
  }

  // Cartelería: los dos venta_tipo comparten origen (negocios_carteleria) y el mismo lookup por
  // folio. 'negocios_carteleria' = gestión/OPEX (índice de meses_facturacion); 'negocios_carteleria_hw'
  // = HW-CAPEX (índice de detalle_pagos_hardware, Variante 1). El match es por folio en ambos
  // JSONB, así que un solo recorrido sirve; solo el TIPO de la key cambia (f.venta_tipo).
  const cartRows = [...(porTipo.negocios_carteleria || []), ...(porTipo.negocios_carteleria_hw || [])];
  if (cartRows.length > 0) {
    const ids = [...new Set(cartRows.map((f) => f.venta_ref_id))];
    const { data } = await client
      .from("negocios_carteleria")
      .select("id, meses_facturacion, detalle_pagos_hardware")
      .in("id", ids);
    for (const row of data || []) {
      for (const f of cartRows.filter((x) => x.venta_ref_id === row.id)) {
        const folio = String(f.folio);
        const meses =
          typeof row.meses_facturacion === "string"
            ? JSON.parse(row.meses_facturacion || "[]")
            : row.meses_facturacion || [];
        const entryMes = meses.find((m) => String(m.facturanumero) === folio);
        if (entryMes) {
          result.set(makeKey(f.venta_tipo, row.id, f.venta_ref_index), {
            plazocobro: entryMes.plazocobro ?? null,
            facturafechacobro: entryMes.facturafechacobro ?? null,
            moneda_dolar_origen: false,
          });
          continue;
        }
        const detalle =
          typeof row.detalle_pagos_hardware === "string"
            ? JSON.parse(row.detalle_pagos_hardware || "[]")
            : row.detalle_pagos_hardware || [];
        const entryHw = detalle.find((d) => String(d.facturanumero) === folio);
        result.set(makeKey(f.venta_tipo, row.id, f.venta_ref_index), {
          plazocobro: entryHw?.plazocobro ?? null,
          facturafechacobro: entryHw?.facturafechacobro ?? null,
          moneda_dolar_origen: false,
        });
      }
    }
  }

  return result;
};
